import { useEffect, useMemo, useRef, useState } from "react";
import { BsSearch, BsX } from "react-icons/bs";

const RECENT_STORAGE_KEY = "raya.recentAccounts";
const MAX_RECENT = 3;
// Below this many orgs the picker stays a plain list: no search box, no recents.
const SEARCH_THRESHOLD = 6;

const readRecent = () => {
  try {
    const raw = localStorage.getItem(RECENT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const rememberRecent = (accountId) => {
  try {
    const next = [accountId, ...readRecent().filter((id) => id !== accountId)].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage unavailable (private mode, quota) — recents are a convenience only
  }
};

const normalise = (value) => (value || "").toString().toLowerCase().trim();

const TenantRow = ({ tenant, active, showRole, logoBroken, onLogoError, onSelect, onHover, rowRef }) => (
  <div
    ref={rowRef}
    role="option"
    aria-selected={active}
    tabIndex={-1}
    className={`tenant-row${active ? " tenant-row--active" : ""}`}
    onClick={() => onSelect(tenant.accountId)}
    onMouseMove={onHover}
    title={tenant.accountName}
  >
    {tenant.companyLogo && !logoBroken ? (
      <img
        src={tenant.companyLogo}
        alt=""
        loading="lazy"
        className="tenant-avatar"
        onError={() => onLogoError(tenant.accountId)}
      />
    ) : (
      <div className="tenant-avatar tenant-avatar--initial">
        {tenant.accountName?.charAt(0)?.toUpperCase() || "?"}
      </div>
    )}
    <span className="tenant-name">{tenant.accountName}</span>
    {showRole && tenant.role && <span className="tenant-role">{tenant.role}</span>}
    <span className="tenant-arrow" aria-hidden="true">→</span>
  </div>
);

export const TenantPicker = ({ tenants, onSelect, onBack, isLoading, email }) => {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [brokenLogos, setBrokenLogos] = useState({});
  const [recentIds] = useState(readRecent);
  const inputRef = useRef(null);
  const activeRowRef = useRef(null);

  const isLarge = tenants.length >= SEARCH_THRESHOLD;
  const showRole = useMemo(() => new Set(tenants.map((t) => t.role)).size > 1, [tenants]);

  const sorted = useMemo(
    () => [...tenants].sort((a, b) => (a.accountName || "").localeCompare(b.accountName || "", undefined, { sensitivity: "base" })),
    [tenants]
  );

  const filtered = useMemo(() => {
    const q = normalise(query);
    if (!q) return sorted;
    return sorted.filter(
      (t) => normalise(t.accountName).includes(q) || normalise(t.slug).includes(q) || normalise(t.role).includes(q)
    );
  }, [sorted, query]);

  const recent = useMemo(() => {
    if (!isLarge || query) return [];
    return recentIds.map((id) => tenants.find((t) => t.accountId === id)).filter(Boolean);
  }, [isLarge, query, recentIds, tenants]);

  // Flat, ordered list of everything rendered — the keyboard cursor walks this.
  const visible = useMemo(() => [...recent, ...filtered], [recent, filtered]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (isLarge) inputRef.current?.focus();
  }, [isLarge]);

  useEffect(() => {
    activeRowRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex]);

  const select = (accountId) => {
    if (isLoading) return;
    rememberRecent(accountId);
    onSelect(accountId);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, visible.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (visible[activeIndex]) select(visible[activeIndex].accountId);
    } else if (e.key === "Escape") {
      if (query) {
        e.preventDefault();
        setQuery("");
      }
    }
  };

  const markLogoBroken = (accountId) => setBrokenLogos((prev) => ({ ...prev, [accountId]: true }));

  const renderRow = (tenant, index) => (
    <TenantRow
      key={`${index < recent.length ? "recent" : "all"}-${tenant.accountId}`}
      tenant={tenant}
      active={index === activeIndex}
      showRole={showRole}
      logoBroken={brokenLogos[tenant.accountId]}
      onLogoError={markLogoBroken}
      onSelect={select}
      onHover={() => setActiveIndex(index)}
      rowRef={index === activeIndex ? activeRowRef : null}
    />
  );

  return (
    <div className="tenant-picker" onKeyDown={handleKeyDown}>
      <div className="login-icon">🏢</div>
      <h2>Choose Organization</h2>
      <p className="mfa-hint">Your account is linked to multiple organizations. Select one to continue.</p>

      {isLarge && (
        <div className="input-group tenant-search">
          <BsSearch className="input-icon" />
          <input
            ref={inputRef}
            type="text"
            className="input-field"
            placeholder={`Search ${tenants.length} organizations…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search organizations"
            autoComplete="off"
          />
          {query && (
            <BsX className="input-icon-right" onClick={() => setQuery("")} aria-label="Clear search" />
          )}
        </div>
      )}

      <div className={`tenant-list${isLarge ? " tenant-list--scroll" : ""}`} role="listbox" aria-label="Organizations">
        {recent.length > 0 && (
          <>
            <div className="tenant-section-label">Recent</div>
            {recent.map((t, i) => renderRow(t, i))}
            <div className="tenant-section-label">All organizations</div>
          </>
        )}

        {filtered.map((t, i) => renderRow(t, recent.length + i))}

        {filtered.length === 0 && (
          <div className="tenant-empty">
            No organizations match “{query}”
          </div>
        )}
      </div>

      {isLarge && (
        <div className="tenant-footer">
          {query ? `${filtered.length} of ${tenants.length} organizations` : `${tenants.length} organizations`}
          <span className="tenant-footer-hint"> · ↑↓ to navigate, Enter to select</span>
        </div>
      )}

      {isLoading && <div className="spinner" />}

      {onBack && (
        <p className="tenant-back">
          {email && <span className="tenant-back-email">{email}</span>}
          <button type="button" className="link-button" onClick={onBack} disabled={isLoading}>
            Use a different account
          </button>
        </p>
      )}
    </div>
  );
};

export default TenantPicker;
